import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import { StatesHub, StateLanding } from "@/pages/LaborPool";
import AuthPage from "@/pages/AuthPage";
import AgencyDashboard from "@/pages/AgencyDashboard";
import CaregiverDashboard from "@/pages/CaregiverDashboard";
import FamilyDashboard from "@/pages/FamilyDashboard";
import PaymentSettings from "@/pages/PaymentSettings";
import DocumentVault from "@/pages/DocumentVault";
import Notifications from "@/pages/Notifications";
import BackgroundMonitor from "@/pages/BackgroundMonitor";
import FacilitySchedule from "@/pages/FacilitySchedule";
import ShiftsPage from "@/pages/ShiftsPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import HelperWidget from "@/components/HelperWidget";
import MessagesDrawer from "@/components/MessagesDrawer";
import ServiceExplorer from "@/pages/ServiceExplorer";
import CoordinatorDashboard from "@/pages/CoordinatorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CoordinatorInvite from "@/pages/CoordinatorInvite";
import BroadcastRespond from "@/pages/BroadcastRespond";
import BroadcastOptout from "@/pages/BroadcastOptout";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataPractices from "@/pages/DataPractices";
import Status from "@/pages/Status";
import CookieConsent from "@/components/CookieConsent";
import LeadCaptureModal from "@/components/LeadCaptureModal";
import { trackPageview } from "@/lib/tracker";
import ErrorBoundary from "@/components/ErrorBoundary";

function PageviewTracker() {
    const loc = useLocation();
    useEffect(() => { trackPageview(loc.pathname); }, [loc.pathname]);
    return null;
}

function Loading() {
    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center">
            <div className="text-sm font-dmsans text-stone-500">Loading…</div>
        </div>
    );
}

function RequireAuth({ children, role }) {
    const { user, loading } = useAuth();
    if (loading) return <Loading />;
    if (!user) return <Navigate to="/auth?mode=login" replace />;
    if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
    return children;
}

function DashboardRouter() {
    const { user, loading } = useAuth();
    if (loading) return <Loading />;
    if (!user) return <Navigate to="/auth?mode=login" replace />;
    if (user.role === "agency") return <AgencyDashboard />;
    if (user.role === "family") return <FamilyDashboard />;
    if (user.role === "coordinator") return <CoordinatorDashboard />;
    if (["admin", "super_admin", "coo", "regional_manager"].includes(user.role)) return <AdminDashboard />;
    return <CaregiverDashboard />;
}

export default function App() {
    return (
        <AuthProvider>
            <ErrorBoundary>
                <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/services/:pillarKey" element={<ServiceExplorer />} />
                    <Route path="/states" element={<StatesHub />} />
                    <Route path="/state/:code" element={<StateLanding />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/coordinator-invite" element={<CoordinatorInvite />} />
                    <Route path="/broadcast/respond" element={<BroadcastRespond />} />
                    <Route path="/broadcast/optout" element={<BroadcastOptout />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/data-practices" element={<DataPractices />} />
                    <Route path="/status" element={<Status />} />
                    <Route path="/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
                    <Route path="/dashboard" element={<DashboardRouter />} />
                    <Route path="/shifts" element={<RequireAuth><ShiftsPage /></RequireAuth>} />
                    <Route path="/payments" element={<RequireAuth role="agency"><PaymentSettings /></RequireAuth>} />
                    <Route path="/vault" element={<RequireAuth><DocumentVault /></RequireAuth>} />
                    <Route path="/notify" element={<RequireAuth><Notifications /></RequireAuth>} />
                    <Route path="/monitor" element={<RequireAuth role="agency"><BackgroundMonitor /></RequireAuth>} />
                    <Route path="/facility" element={<RequireAuth role="agency"><FacilitySchedule /></RequireAuth>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <HelperWidget />
                <MessagesDrawer />
                <CookieConsent />
                <LeadCaptureModal />
                <PageviewTracker />
                <Toaster position="top-right" richColors closeButton />
            </BrowserRouter>
            </ErrorBoundary>
        </AuthProvider>
    );
}
